// pages/AgreementStatusPage.jsx
// Broker-facing view of where things stand with this company's introducer
// agreement. Three states: nothing set up (with an in-app upload — replaces
// emailing support@brokerconnect.co.uk, 7 July 2026), submitted and awaiting
// the platform team's review, or live. Template management itself stays
// platform-owner-only (/owner/agreements) — this page is read-only plus the
// one upload action.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from '../components/Sidebar';

const token = () => localStorage.getItem('token');
const API = process.env.REACT_APP_API_URL || 'https://brokerconnect.co.uk/api';

export default function AgreementStatusPage() {
  const [loading, setLoading]     = useState(true);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [dragOver, setDragOver]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInput = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, submissionRes] = await Promise.all([
        fetch(`${API}/agreement-templates/workflow-status`, { headers: { Authorization: `Bearer ${token()}` } }),
        fetch(`${API}/agreement-templates/my-submission`, { headers: { Authorization: `Bearer ${token()}` } }),
      ]);
      const statusData = await statusRes.json().catch(() => ({}));
      const submissionData = await submissionRes.json().catch(() => ({}));
      setActiveTemplate(statusData.activeTemplate || null);
      setSubmission(submissionData.submission || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploadError('');
    setUploading(true);
    const form = new FormData();
    form.append('template', file);
    form.append('display_name', displayName || file.name.replace(/\.[^.]+$/, ''));
    try {
      const res = await fetch(`${API}/agreement-templates/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setUploadError(data.error || 'Upload failed'); return; }
      setDisplayName('');
      await load();
    } catch {
      setUploadError('Network error — please try again.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#F7FAFC' }}>
      <Sidebar />
      <main className="main-content" style={{ padding:'32px 40px', maxWidth:720 }}>
        <h1 style={{ margin:'0 0 6px', fontSize:22, fontWeight:800, color:'#0e1b2c' }}>
          Introducer Agreements
        </h1>
        <p style={{ margin:'0 0 24px', fontSize:13, color:'#718096' }}>
          Agreement templates are set up and approved by the BrokerConnect team, working from your
          network's documentation, then pushed live to your company.
        </p>

        {loading ? (
          <p style={{ fontSize:13, color:'#718096' }}>Loading…</p>
        ) : activeTemplate ? (
          <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:24 }}>
            <div style={{
              display:'inline-block', marginBottom:16, padding:'4px 12px', borderRadius:20,
              background:'#f0fdf4', border:'1px solid #86efac', color:'#166534',
              fontSize:12, fontWeight:700,
            }}>
              ✓ Live — introducers will complete this when they sign up
            </div>
            <p style={{ margin:0, fontSize:14, fontWeight:600, color:'#0e1b2c' }}>
              {activeTemplate.display_name}
            </p>
            <p style={{ margin:'2px 0 0', fontSize:12, color:'#94a3b8' }}>
              v{activeTemplate.version}
            </p>
          </div>
        ) : submission ? (
          <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:24 }}>
            <div style={{
              display:'inline-block', marginBottom:16, padding:'4px 12px', borderRadius:20,
              background:'#eff6ff', border:'1px solid #93c5fd', color:'#1d4ed8',
              fontSize:12, fontWeight:700,
            }}>
              Submitted — awaiting review
            </div>
            <p style={{ margin:0, fontSize:14, fontWeight:600, color:'#0e1b2c' }}>
              {submission.display_name}
            </p>
            <p style={{ margin:'6px 0 0', fontSize:13, color:'#718096', lineHeight:1.6 }}>
              Submitted {new Date(submission.created_at).toLocaleDateString()}. Our team will review it and set
              up an approved digital version — we'll let you know once it's ready. No need to do anything else
              in the meantime; new introducers can still be invited without a digital agreement.
            </p>
          </div>
        ) : (
          <>
            <div style={{
              background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:12, padding:24, marginBottom:20,
            }}>
              <p style={{ margin:'0 0 8px', fontWeight:700, color:'#92400e', fontSize:14 }}>
                You do not currently have any approved introducer agreement templates set up
              </p>
              <p style={{ margin:0, fontSize:13, color:'#78350f', lineHeight:1.6 }}>
                Upload your network's introducer agreement below and we'll work with your network to get an
                automated, digital version approved for your company. Until then, new introducers are invited
                without a digital agreement — or email{' '}
                <a href="mailto:support@brokerconnect.co.uk" style={{ color:'#92400e' }}>support@brokerconnect.co.uk</a> if
                you'd rather send it that way.
              </p>
            </div>

            <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:24 }}>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#4b5a6e', marginBottom:6 }}>
                Agreement name <span style={{ color:'#94a3b8', fontWeight:400 }}>(optional — helps us tell it apart, e.g. "Sesame Introducer Agreement")</span>
              </label>
              <input
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="e.g. Sesame Introducer Agreement"
                style={{
                  width:'100%', boxSizing:'border-box', marginBottom:14,
                  padding:'10px 12px', borderRadius:8,
                  border:'1px solid #e2e8f0', fontSize:13,
                  color:'#2d3748', outline:'none',
                }}
              />
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files[0]); }}
                onClick={() => !uploading && fileInput.current.click()}
                style={{
                  border: `2px dashed ${dragOver ? '#10b981' : '#cbd5e0'}`,
                  borderRadius:10, padding:32, textAlign:'center',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  background: dragOver ? '#f0fdf4' : '#f7fafc',
                  opacity: uploading ? 0.6 : 1,
                }}
              >
                <input
                  ref={fileInput} type="file" accept=".pdf,.doc,.docx"
                  style={{ display:'none' }}
                  onChange={e => handleUpload(e.target.files[0])}
                  disabled={uploading}
                />
                <div style={{ fontSize:36, marginBottom:8 }}>{uploading ? '⏳' : '📄'}</div>
                <p style={{ margin:'0 0 4px', fontWeight:600, color:'#4b5a6e' }}>
                  {uploading ? 'Uploading…' : 'Drop your agreement here, or click to browse'}
                </p>
                <p style={{ margin:0, fontSize:12, color:'#a0aec0' }}>PDF or Word (.docx) — max 20 MB</p>
              </div>
              {uploadError && (
                <div style={{ marginTop:12, padding:'10px 14px', borderRadius:8,
                  background:'#fef2f2', border:'1px solid #fca5a5', fontSize:13, color:'#991b1b' }}>
                  ⚠️ {uploadError}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
